'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import LoadingSkeleton from '@/components/animate-ui/loading-skeleton'
import LoadingSpinner from '@/components/animate-ui/loading-spinner'
import { cn } from '@/lib/utils'

interface LoadingCardProps {
  variant?: 'skeleton' | 'spinner' | 'pulse'
  className?: string
  title?: string
  description?: string
  rows?: number
  showHeader?: boolean
}

export default function LoadingCard({
  variant = 'skeleton',
  className,
  title = 'Loading...',
  description,
  rows = 3,
  showHeader = true,
}: LoadingCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className={cn('overflow-hidden', className)}>
        {showHeader && (
          <CardHeader className="space-y-2">
            {variant === 'skeleton' ? (
              <>
                <LoadingSkeleton variant="text" width="60%" height="20px" />
                {description && (
                  <LoadingSkeleton variant="text" width="80%" height="16px" />
                )}
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <LoadingSpinner 
                    size="sm" 
                    variant={variant === 'spinner' ? 'default' : 'pulse'} 
                  />
                  <span className="text-sm font-medium">{title}</span>
                </div>
                {description && (
                  <p className="text-sm text-muted-foreground">{description}</p>
                )}
              </div>
            )}
          </CardHeader>
        )}
        
        <CardContent className="space-y-3">
          {variant === 'skeleton' ? (
            <>
              {Array.from({ length: rows }).map((_, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="space-y-2"
                >
                  <LoadingSkeleton 
                    variant="text" 
                    width={`${Math.random() * 40 + 60}%`} 
                    height="16px" 
                  />
                  <LoadingSkeleton 
                    variant="text" 
                    width={`${Math.random() * 30 + 70}%`} 
                    height="14px" 
                  />
                </motion.div>
              ))}
            </>
          ) : (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner 
                size="lg" 
                variant={variant === 'spinner' ? 'default' : 'dots'}
                text="Loading data..."
              />
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}